import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SeoHead from "@/components/feature/SeoHead";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

function formatNumber(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter(
        (a) => a.category === article.category && a.id !== article.id
      )
      .slice(0, 4);
  }, [article]);

  const breadcrumbItems = article
    ? [
        { label: "首页", href: "/" },
        { label: article.categoryName, href: `/category/${article.category}` },
        { label: article.title },
      ]
    : [
        { label: "首页", href: "/" },
        { label: "文章详情" },
      ];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SeoHead
          title="文章未找到 - 黑料在线"
          description="抱歉，您访问的文章不存在或已被删除。"
          canonicalUrl="http://lvepkfa.cn/"
        />
        <Navbar />
        <main className="pt-20 md:pt-24 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-close-line text-3xl text-foreground-400" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              抱歉，您访问的文章不存在或已被删除。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "黑料在线",
      logo: {
        "@type": "ImageObject",
        url: "http://lvepkfa.cn/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `http://lvepkfa.cn/article/${article.id}`,
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(","),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://lvepkfa.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: article.categoryName,
        item: `http://lvepkfa.cn/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: `http://lvepkfa.cn/article/${article.id}`,
      },
    ],
  };

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: article.title,
    url: `http://lvepkfa.cn/article/${article.id}`,
    description: article.summary,
    isPartOf: {
      "@type": "WebSite",
      name: "黑料在线",
      url: "http://lvepkfa.cn/",
    },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: article.coverImage,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${article.title} - 黑料在线`}
        description={article.summary}
        keywords={`${article.tags.join(",")},黑料在线,每日黑料吃瓜网,在线看黑料`}
        canonicalUrl={`http://lvepkfa.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={[articleSchema, breadcrumbSchema, webpageSchema]}
      />

      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Article Header */}
        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 max-w-4xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="px-4 md:px-6 lg:px-8 pb-8 md:pb-12 max-w-4xl mx-auto" itemScope itemType="https://schema.org/Article">
          {/* Cover Image */}
          <div className="rounded-xl overflow-hidden mb-6 md:mb-8 aspect-[16/9]">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              width={800}
              height={450}
              itemProp="image"
            />
          </div>

          {/* Meta */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Link
              to={`/category/${article.category}`}
              className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-100 text-accent-700"
            >
              {article.categoryName}
            </Link>
            <span className="text-xs text-foreground-400">{article.publishDate}</span>
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatNumber(article.viewCount)}次阅读
            </span>
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-time-line" />
              {article.readTime}分钟
            </span>
          </div>

          <h1
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 md:mb-6 leading-tight"
            itemProp="headline"
          >
            {article.title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-3 mb-6 md:mb-8 pb-6 md:pb-8 border-b border-background-200/70">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
              {article.author.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800" itemProp="author">
                {article.author}
              </p>
              <p className="text-xs text-foreground-400">黑料在线特约撰稿人</p>
            </div>
          </div>

          {/* Content */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
            itemProp="articleBody"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm font-medium text-foreground-500">分享到：</span>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="微博分享">
              <i className="ri-weibo-fill text-sm" />
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="微信分享">
              <i className="ri-wechat-fill text-sm" />
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="复制链接">
              <i className="ri-link text-sm" />
            </button>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-200/60 transition-colors cursor-pointer"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                        width={80}
                        height={80}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 line-clamp-1">
                        {ra.summary}
                      </p>
                      <span className="text-xs text-foreground-400 mt-1 block">
                        {ra.publishDate}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}